import { AxiosError } from 'axios';
import { notification } from 'ant-design-vue';
import i18n from '@/i18n';
import Notification from '@/modules/Notifications/api/models/Notification';
import { LocalStorageService } from '@/services/LocalStorageService';
import { LocalStorageKeyNames } from '@/enums/global/LocalStorageKeyNames';

/**
 * Show error notifications from given error
 * @param error - The error that occurred
 */
export function showErrorNotifications(error: AxiosError, duration = 5) {
    // Skip if already handled by axios interceptor
    if ((error as any)._notificationShown) {
        return;
    }

    if (error?.response == null) {
        const errorMessage = (error as Error).message;

        // Check if message contains HTML with onclick for clickable help icon
        if (errorMessage.includes('onclick=')) {
            notification.error({
                message: i18n.t('Dogodila se greška') as string,
                description: (h: any) => h('div', { domProps: { innerHTML: errorMessage } }),
                duration: 0, // Don't auto-dismiss
            });
            return;
        }

        notification.error({
            message: i18n.t('Dogodila se greška') as string,
            description: errorMessage,
            duration,
        });
        return;
    }

    error.response.data.errors.forEach(({ detail }: { detail: string }) => {
        notification.error({
            message: i18n.t('Dogodila se greška') as string,
            description: detail,
            duration,
        });
    });
}

/**
 * Show the successful change notification
 */
export function showChangeSuccessNotification() {
    notification.success({
        message: i18n.t('Promjena uspješna!') as string,
        description: '',
    });
}

// Timer reference for notification polling
let notificationIntervalId: number | null = null;

/**
 * Update unread notification count from the server
 * @returns Promise that resolves when the count is updated
 */
export async function updateUnreadNotificationCount(): Promise<void> {
    // Skip if user is not logged in
    if (!LocalStorageService.has(LocalStorageKeyNames.token)) {
        return;
    }

    try {
        const unreadCount = await Notification.getUnreadCount();
        const store = await import('@/store');
        store.default.dispatch('generic/updateUnreadNotifications', unreadCount.unread);
    } catch {
        // Silently fail - 401 errors are handled by axios interceptor
        // and will redirect to login if needed
    }
}

/**
 * Start the notification polling timer
 * Polls for unread notifications every 5 minutes (300000ms)
 */
export function startNotificationPolling(): void {
    if (notificationIntervalId) {
        return;
    }

    updateUnreadNotificationCount();

    notificationIntervalId = window.setInterval(() => {
        updateUnreadNotificationCount();
    }, 300000);
}

/**
 * Clear the notification polling timer
 */
export function clearNotificationPolling(): void {
    if (notificationIntervalId) {
        clearInterval(notificationIntervalId);
        notificationIntervalId = null;
    }
}

/**
 * Initialize notification functions
 * Updates the unread count immediately and starts polling
 */
export function initializeNotifications(): void {
    startNotificationPolling();
}
